import { useCallback, useEffect, useState } from 'react';
import { useZeroTTS } from './useZeroTTS';
import { clearAllZeroTTSCaches } from './zeroTTSManifest';
import { KOKORO_VOICES } from './voiceMapping';
import type { VoiceIdentity, ZeroTTSDepth } from './zeroTTSProfile';
import type {
  KokoroBlendMode,
  KokoroGender,
  VoiceSpec,
} from './types';

export interface ZeroTTSSettingsPanelProps {
  personaName: string;
  worldSeedHex: string;
  defaultGender: KokoroGender;
  enabled: boolean;
  onToggleEnabled: (next: boolean) => Promise<void> | void;
  fireAck: boolean;
  fireToolInvoke: boolean;
  fireProgress: boolean;
  fireError: boolean;
  onToggleFireAck: (next: boolean) => Promise<void> | void;
  onToggleFireToolInvoke: (next: boolean) => Promise<void> | void;
  onToggleFireProgress: (next: boolean) => Promise<void> | void;
  onToggleFireError: (next: boolean) => Promise<void> | void;
  /** Stage 26.6: global blend mode + per-persona pinned VoiceSpec. */
  blendMode?: KokoroBlendMode;
  pinnedSpec?: VoiceSpec | null;
  /** Stage 40: cache depth (classic / medium / long). */
  depth?: ZeroTTSDepth;
  onDepthChange?: (next: ZeroTTSDepth) => Promise<void> | void;
}

function formatIdentity(id: VoiceIdentity | null): string {
  if (!id) return '—';
  if (id.kind === 'single') return `single: ${id.voiceId}`;
  const a = KOKORO_VOICES[id.spec.voiceA] ?? `#${id.spec.voiceA}`;
  const b = KOKORO_VOICES[id.spec.voiceB] ?? `#${id.spec.voiceB}`;
  return `blend: ${a}×${b}@${id.spec.t.toFixed(2)} p=${id.spec.pitch.toFixed(2)} e=${id.spec.energy.toFixed(2)}`;
}

function formatBytes(n: number): string {
  if (n <= 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function ZeroTTSSettingsPanel(
  props: ZeroTTSSettingsPanelProps,
): JSX.Element {
  const {
    personaName,
    worldSeedHex,
    defaultGender,
    enabled,
    onToggleEnabled,
    fireAck,
    fireToolInvoke,
    fireProgress,
    fireError,
    onToggleFireAck,
    onToggleFireToolInvoke,
    onToggleFireProgress,
    onToggleFireError,
    blendMode = 'single',
    pinnedSpec = null,
    depth = 'classic',
    onDepthChange,
  } = props;

  const tts = useZeroTTS({
    personaName,
    worldSeedHex,
    defaultGender,
    enabled,
    blendMode,
    pinnedSpec,
    depth,
  });
  const [busy, setBusy] = useState(false);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');

  // ARIA live-region: announce install progress every ~10%.
  useEffect(() => {
    if (!tts.progress) return;
    const { done, total } = tts.progress;
    if (total <= 0) return;
    const pct = Math.floor((done / total) * 10) * 10;
    setLiveAnnouncement(
      `ZeroTTS install for ${personaName || 'persona'}: ${pct}% complete`,
    );
  }, [tts.progress, personaName]);

  const run = useCallback(async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  }, []);

  return (
    <section
      aria-labelledby="zerotts-panel-heading"
      style={{
        marginTop: 24,
        padding: 12,
        border: '1px solid var(--border, #333)',
        borderRadius: 6,
      }}
    >
      <h3 id="zerotts-panel-heading" style={{ margin: '0 0 8px 0' }}>
        ZeroTTS — Cached Persona Speech
      </h3>
      <p style={{ marginTop: 0, opacity: 0.8, fontSize: '0.9em' }}>
        Pre-renders acknowledgments and tool-invocation phrases for the active
        persona, then plays them instantly from browser Cache Storage while
        the real Kokoro reply is being generated. The cached filler is cut off
        as soon as the real audio starts.
      </p>

      <label
        style={{
          display: 'block',
          margin: '12px 0',
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => void onToggleEnabled(e.target.checked)}
        />{' '}
        Enable instant-speech cache
      </label>

      <fieldset
        disabled={!enabled}
        style={{
          border: 'none',
          padding: 0,
          margin: '0 0 12px 0',
          opacity: enabled ? 1 : 0.6,
        }}
      >
        <legend style={{ fontSize: '0.9em', padding: 0 }}>
          Fire cached filler on:
        </legend>
        <label style={{ marginRight: 12 }}>
          <input
            type="checkbox"
            checked={fireAck}
            onChange={(e) => void onToggleFireAck(e.target.checked)}
          />{' '}
          ack
        </label>
        <label style={{ marginRight: 12 }}>
          <input
            type="checkbox"
            checked={fireToolInvoke}
            onChange={(e) => void onToggleFireToolInvoke(e.target.checked)}
          />{' '}
          tool-start
        </label>
        <label style={{ marginRight: 12 }}>
          <input
            type="checkbox"
            checked={fireProgress}
            onChange={(e) => void onToggleFireProgress(e.target.checked)}
          />{' '}
          progress
        </label>
        <label>
          <input
            type="checkbox"
            checked={fireError}
            onChange={(e) => void onToggleFireError(e.target.checked)}
          />{' '}
          error
        </label>
      </fieldset>

      {/* Stage 40: 3-way cache-depth toggle. Switching depth changes
          the voice fingerprint, which puts the persona in state 'idle'
          ("not installed"); the user clicks Install to regenerate. */}
      <fieldset
        disabled={!enabled}
        style={{
          border: '1px solid var(--border-soft, #2a2a2e)',
          padding: '6px 10px',
          margin: '0 0 12px 0',
          opacity: enabled ? 1 : 0.6,
        }}
      >
        <legend style={{ fontSize: '0.85em', opacity: 0.75, padding: '0 6px' }}>
          Cache depth (regenerate after changing)
        </legend>
        {(['classic', 'medium', 'long'] as const).map((d) => (
          <label key={d} style={{ marginRight: 12 }}>
            <input
              type="radio"
              name="zerotts-depth"
              value={d}
              checked={depth === d}
              onChange={() => {
                if (onDepthChange) void onDepthChange(d);
              }}
            />{' '}
            {d}
          </label>
        ))}
      </fieldset>

      <div style={{ margin: '12px 0' }}>
        <strong>{personaName || '(no active persona)'}</strong>
        {' — '}
        {tts.state === 'ready' && (
          <>
            {tts.atomCount} atoms · {formatBytes(tts.cacheBytes)} · ready
            <div
              style={{ fontSize: '0.85em', opacity: 0.75, marginTop: 2 }}
              title="Voice the atom cache was rendered with. Repin or switch blend mode to re-render."
            >
              {formatIdentity(tts.voiceIdentity)}
            </div>
          </>
        )}
        {tts.state === 'installing' && tts.progress && (
          <>
            installing {tts.progress.done} / {tts.progress.total} (
            {Math.round(
              (100 * tts.progress.done) / Math.max(1, tts.progress.total),
            )}
            %)
          </>
        )}
        {tts.state === 'loading_profile' && 'loading profile…'}
        {tts.state === 'idle' && (
          <>
            not installed
            {tts.voiceIdentity ? (
              <div
                style={{ fontSize: '0.85em', opacity: 0.75, marginTop: 2 }}
              >
                next install voice: {formatIdentity(tts.voiceIdentity)}
              </div>
            ) : null}
          </>
        )}
        {tts.state === 'unavailable' &&
          'unavailable (no profile or Cache Storage)'}
        {tts.state === 'disabled' && 'disabled'}
        {tts.state === 'error' && `error: ${tts.error ?? 'unknown'}`}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(tts.state === 'idle' || tts.state === 'error') && (
          <button
            type="button"
            disabled={busy || !tts.personaSeed || !enabled}
            onClick={() => run(tts.install)}
            aria-label={`Install ZeroTTS for ${personaName || 'persona'}`}
          >
            {tts.state === 'error' ? 'Retry install' : 'Install'}
          </button>
        )}
        {tts.state === 'ready' && (
          <>
            <button
              type="button"
              disabled={busy}
              onClick={() => run(tts.reinstall)}
              aria-label={`Reinstall ZeroTTS for ${personaName || 'persona'}`}
            >
              Reinstall
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => run(tts.clearCache)}
              aria-label={`Clear ZeroTTS cache for ${personaName || 'persona'}`}
            >
              Clear
            </button>
          </>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <button
          type="button"
          disabled={busy}
          onClick={() =>
            run(async () => {
              await clearAllZeroTTSCaches();
              // Hard reload so every hook reinitialises from scratch.
              window.location.reload();
            })
          }
          aria-label="Clear all ZeroTTS caches across every persona"
        >
          Clear all ZeroTTS caches
        </button>
      </div>

      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
        }}
      >
        {liveAnnouncement}
      </div>
    </section>
  );
}
